/**
 * SchematicGeneratorPanel — v2.44 — CSV-driven mass drawing generator.
 *
 * Workflow (matches backend /api/schematic-gen):
 *   1. POST /sessions to create a session (once per visit / re-uploaded).
 *   2. Upload CSV + template DWG/DXFs via /sessions/{id}/upload (multipart).
 *   3. Server auto-validates on every /sessions/{id} GET.
 *   4. If ok → POST /generate → server produces ZIP into GridFS.
 *   5. Download ZIP via /sessions/{id}/download.
 *
 * Users can also:
 *   • Download a sample CSV + sample DXF template (public GETs).
 *   • Save any uploaded template into a persistent per-user library.
 *   • Re-import a library template into the current session with 1 click.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  UploadSimple, DownloadSimple, Spinner, X, Warning, CheckCircle,
  BookOpen, FileCsv, ArrowClockwise, FloppyDisk, Trash, Package,
  ShareNetwork, FolderOpen, Eye, Table, GitFork, CaretDown,
} from "@phosphor-icons/react";
import { ALLOWED_EXTS, getBlob, downloadBlob } from "./SchematicGenerator/utils";
import { DiffTable, StatCard } from "./SchematicGenerator/DiffTable";

export default function SchematicGeneratorPanel() {
  const [sessionId, setSessionId] = useState(null);
  const [session, setSession] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [library, setLibrary] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [packs, setPacks] = useState([]);
  const [packsLoading, setPacksLoading] = useState(false);
  // v2.52 — Org CSV starter templates
  const [orgCsvs, setOrgCsvs] = useState([]);
  const [orgCsvsLoading, setOrgCsvsLoading] = useState(false);
  const [forkPackMenuFor, setForkPackMenuFor] = useState(null); // csv id whose "+pack" menu is open
  const [showPinCsvModal, setShowPinCsvModal] = useState(false);
  const [pinCsvName, setPinCsvName] = useState("");
  const [pinCsvDesc, setPinCsvDesc] = useState("");
  const [pinCsvFile, setPinCsvFile] = useState(null);
  const [pinningCsv, setPinningCsv] = useState(false);
  const [showSavePackModal, setShowSavePackModal] = useState(false);
  const [savingPack, setSavingPack] = useState(false);
  const [packName, setPackName] = useState("");
  const [packDescription, setPackDescription] = useState("");
  // v2.46 — Batch progress state (per-drawing status from SSE stream)
  const [progress, setProgress] = useState(null); // { drawings:{dwg:{status,subs,error}}, complete, downloadUrl }
  const [dragOver, setDragOver] = useState(false);
  // v2.47 — Dry-run preview modal state
  const [previewing, setPreviewing] = useState(false);
  const [previewData, setPreviewData] = useState(null); // {png_base64, drawing_no, substitutions, template_name, columns, row_values, all_rows}
  const [showDiff, setShowDiff] = useState(false);
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const libInputRef = useRef(null);
  const pinCsvInputRef = useRef(null);

  // Bootstrap a session on mount (cheap — one POST)
  const bootstrap = useCallback(async () => {
    try {
      const { data } = await api.post("/schematic-gen/sessions");
      setSessionId(data.id);
      setSession(data);
    } catch (e) {
      toast.error(`Couldn't create session: ${e?.response?.data?.detail || e?.message}`);
    }
  }, []);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  const refreshSession = useCallback(async () => {
    if (!sessionId) return;
    try {
      const { data } = await api.get(`/schematic-gen/sessions/${sessionId}`);
      setSession(data);
    } catch (e) {
      toast.error(`Session refresh failed: ${e?.response?.data?.detail || e?.message}`);
    }
  }, [sessionId]);

  const refreshLibrary = useCallback(async () => {
    setLibraryLoading(true);
    try {
      const { data } = await api.get("/schematic-gen/library/templates");
      setLibrary(data.items || []);
    } catch (e) {
      // Non-blocking
    } finally {
      setLibraryLoading(false);
    }
  }, []);

  useEffect(() => { refreshLibrary(); }, [refreshLibrary]);

  const refreshPacks = useCallback(async () => {
    setPacksLoading(true);
    try {
      const { data } = await api.get("/schematic-gen/packs");
      setPacks(data.items || []);
    } catch (e) {
      // Non-blocking — user may not belong to any org yet
    } finally {
      setPacksLoading(false);
    }
  }, []);

  useEffect(() => { refreshPacks(); }, [refreshPacks]);

  const refreshOrgCsvs = useCallback(async () => {
    setOrgCsvsLoading(true);
    try {
      const { data } = await api.get("/schematic-gen/org-csvs");
      setOrgCsvs(data.items || []);
    } catch {
      /* non-blocking — user may not belong to an org */
    } finally {
      setOrgCsvsLoading(false);
    }
  }, []);

  useEffect(() => { refreshOrgCsvs(); }, [refreshOrgCsvs]);

  const onPinOrgCsv = useCallback(async () => {
    if (!pinCsvFile || !pinCsvName.trim()) return;
    setPinningCsv(true);
    try {
      const form = new FormData();
      form.append("file", pinCsvFile);
      const { data } = await api.post("/schematic-gen/org-csvs", form, {
        headers: { "Content-Type": "multipart/form-data" },
        params: {
          name: pinCsvName.trim(),
          description: pinCsvDesc.trim() || undefined,
        },
      });
      toast.success(`Pinned CSV starter '${data.name}'.`);
      setShowPinCsvModal(false);
      setPinCsvName("");
      setPinCsvDesc("");
      setPinCsvFile(null);
      if (pinCsvInputRef.current) pinCsvInputRef.current.value = "";
      await refreshOrgCsvs();
    } catch (e) {
      toast.error(`Pin CSV failed: ${e?.response?.data?.detail || e?.message}`);
    } finally {
      setPinningCsv(false);
    }
  }, [pinCsvFile, pinCsvName, pinCsvDesc, refreshOrgCsvs]);

  const onAttachOrgCsv = useCallback(async (cid, starterName) => {
    if (!sessionId) return;
    try {
      await api.post(
        `/schematic-gen/sessions/${sessionId}/attach-org-csv/${cid}`);
      toast.success(`Loaded '${starterName}' into session.`);
      await refreshSession();
    } catch (e) {
      toast.error(`Attach failed: ${e?.response?.data?.detail || e?.message}`);
    }
  }, [sessionId, refreshSession]);

  const onForkFromOrgCsv = useCallback(async (cid, starterName, packId = null) => {
    // v2.53 — atomic "new session pre-seeded with this starter" flow
    // v2.54 — optional packId also imports templates from an org pack
    try {
      const { data } = await api.post(
        `/schematic-gen/org-csvs/${cid}/fork-session`,
        null,
        packId ? { params: { pack_id: packId } } : undefined);
      setSessionId(data.session.id);
      setSession(data.session);
      // Clear any lingering local state from the previous session
      setProgress(null);
      setPreviewData(null);
      setShowDiff(false);
      const packTail = data.pack_name
        ? ` + '${data.pack_name}' (${data.template_count} template${
            data.template_count === 1 ? "" : "s"})`
        : "";
      toast.success(
        `Forked new session from '${starterName}'${packTail} — ${
          data.template_count > 0 ? "ready to generate." : "ready for templates."
        }`);
    } catch (e) {
      toast.error(`Fork failed: ${e?.response?.data?.detail || e?.message}`);
    }
  }, []);

  const onDownloadOrgCsv = useCallback(async (cid, filename) => {
    try {
      const blob = await getBlob(`/schematic-gen/org-csvs/${cid}/download`);
      downloadBlob(blob, filename || "starter.csv");
    } catch (e) {
      toast.error(`Download failed: ${e?.response?.data?.detail || e?.message}`);
    }
  }, []);

  const onDeleteOrgCsv = useCallback(async (cid, name) => {
    if (!window.confirm(`Delete CSV starter '${name}'? Everyone in the org loses access.`)) return;
    try {
      await api.delete(`/schematic-gen/org-csvs/${cid}`);
      await refreshOrgCsvs();
    } catch (e) {
      toast.error(`Delete failed: ${e?.response?.data?.detail || e?.message}`);
    }
  }, [refreshOrgCsvs]);

  const onSaveAsPack = useCallback(async () => {
    if (!sessionId || !packName.trim()) return;
    setSavingPack(true);
    try {
      const { data } = await api.post(
        `/schematic-gen/sessions/${sessionId}/save-as-pack`,
        null,
        { params: { name: packName.trim(),
                       description: packDescription.trim() || undefined } });
      toast.success(
        `Saved pack '${data.name}' — ${data.template_count} template(s).`);
      setShowSavePackModal(false);
      setPackName("");
      setPackDescription("");
      await refreshPacks();
    } catch (e) {
      toast.error(`Save pack failed: ${e?.response?.data?.detail || e?.message}`);
    } finally {
      setSavingPack(false);
    }
  }, [sessionId, packName, packDescription, refreshPacks]);

  const onImportPack = useCallback(async (packId) => {
    if (!sessionId) return;
    try {
      const { data } = await api.post(
        `/schematic-gen/sessions/${sessionId}/import-pack/${packId}`);
      toast.success(
        `Imported pack '${data.pack_name}' — ${data.imported.templates.length} template(s) added.`);
      await refreshSession();
    } catch (e) {
      toast.error(`Import failed: ${e?.response?.data?.detail || e?.message}`);
    }
  }, [sessionId, refreshSession]);

  const onDownloadPack = useCallback(async (packId, name) => {
    try {
      const blob = await getBlob(
        `/schematic-gen/packs/${packId}/download`);
      downloadBlob(blob, `${name}.zip`);
    } catch (e) {
      toast.error(`Download failed: ${e?.response?.data?.detail || e?.message}`);
    }
  }, []);

  const onDeletePack = useCallback(async (packId, name) => {
    if (!window.confirm(`Delete pack '${name}'? This affects everyone in your org.`)) return;
    try {
      await api.delete(`/schematic-gen/packs/${packId}`);
      await refreshPacks();
    } catch (e) {
      toast.error(`Delete failed: ${e?.response?.data?.detail || e?.message}`);
    }
  }, [refreshPacks]);

  const handleFiles = useCallback(async (fileList) => {
    if (!sessionId || !fileList?.length) return;
    // Client-side extension guard so the user gets immediate feedback
    const bad = Array.from(fileList).filter((f) => !ALLOWED_EXTS.test(f.name));
    if (bad.length) {
      toast.error(
        `Only .csv, .dwg and .dxf files are allowed. Rejected: ${
          bad.map((b) => b.name).slice(0, 3).join(", ")
        }${bad.length > 3 ? "…" : ""}`,
      );
    }
    const ok = Array.from(fileList).filter((f) => ALLOWED_EXTS.test(f.name));
    if (!ok.length) return;
    setUploading(true);
    try {
      const form = new FormData();
      ok.forEach((f) => form.append("files", f));
      const { data } = await api.post(
        `/schematic-gen/sessions/${sessionId}/upload`, form,
        { headers: { "Content-Type": "multipart/form-data" } });
      const nAccepted = (data.accepted || []).length;
      const nRejected = (data.rejected || []).length;
      if (nAccepted) toast.success(`Uploaded ${nAccepted} file${nAccepted === 1 ? "" : "s"}.`);
      if (nRejected) toast.error(
        `${nRejected} file(s) rejected — ${data.rejected[0].reason}`,
      );
      await refreshSession();
    } catch (e) {
      toast.error(`Upload failed: ${e?.response?.data?.detail || e?.message}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [sessionId, refreshSession]);

  const removeFile = useCallback(async (filename) => {
    if (!sessionId) return;
    try {
      await api.delete(
        `/schematic-gen/sessions/${sessionId}/files/${encodeURIComponent(filename)}`,
      );
      await refreshSession();
    } catch (e) {
      toast.error(`Remove failed: ${e?.response?.data?.detail || e?.message}`);
    }
  }, [sessionId, refreshSession]);

  // v2.46 — Recursively walk a dropped folder tree via the HTML5
  // FileSystem-Entry API. Chrome/Edge/Firefox all support it via
  // webkitGetAsEntry() on DataTransferItem. Returns a flat File[].
  const readEntryAsFiles = useCallback((entry, path = "") => {
    return new Promise((resolve) => {
      if (entry.isFile) {
        entry.file((file) => {
          // Preserve the subpath so drawing_no matching still works
          // (uses basename anyway, but nice for debugging).
          try { file._path = path + file.name; } catch { /* frozen */ }
          resolve([file]);
        }, () => resolve([]));
      } else if (entry.isDirectory) {
        const reader = entry.createReader();
        const all = [];
        const readBatch = () => reader.readEntries(async (batch) => {
          if (!batch.length) { resolve(all); return; }
          for (const child of batch) {
            const files = await readEntryAsFiles(
              child, `${path}${entry.name}/`);
            all.push(...files);
          }
          readBatch();
        }, () => resolve(all));
        readBatch();
      } else {
        resolve([]);
      }
    });
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    setDragOver(false);
    const items = e.dataTransfer?.items || [];
    const collected = [];
    // If any entry is a folder, walk them recursively — otherwise the
    // plain files list is fine.
    let hasFolder = false;
    for (let i = 0; i < items.length; i++) {
      const entry = items[i].webkitGetAsEntry?.();
      if (entry?.isDirectory) { hasFolder = true; break; }
    }
    if (hasFolder && items[0]?.webkitGetAsEntry) {
      for (let i = 0; i < items.length; i++) {
        const entry = items[i].webkitGetAsEntry?.();
        if (!entry) continue;
        const files = await readEntryAsFiles(entry);
        collected.push(...files);
      }
      // Convert File[] into a FileList-like array
      handleFiles(collected);
    } else {
      handleFiles(e.dataTransfer.files);
    }
  }, [handleFiles, readEntryAsFiles]);

  // v2.46 — Streaming generate: consumes the SSE event stream to render
  // a live per-drawing progress card. Falls back to the batch endpoint
  // if the browser can't do EventSource / fetch-stream (rare).
  const onGenerate = useCallback(async () => {
    if (!sessionId) return;
    setGenerating(true);
    setProgress({ drawings: {}, order: [], complete: false, downloadUrl: null });
    try {
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const resp = await fetch(
        `${backend}/api/schematic-gen/sessions/${sessionId}/generate-stream`,
        { method: "POST", credentials: "include" });
      if (!resp.ok || !resp.body) {
        throw new Error(`HTTP ${resp.status}`);
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let downloadUrl = null;
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // Split on SSE event delimiter (blank line)
        const chunks = buffer.split(/\n\n/);
        buffer = chunks.pop() || ""; // hold incomplete
        for (const chunk of chunks) {
          const eventLine = chunk.match(/^event:\s*(\S+)/m);
          const dataLine = chunk.match(/^data:\s*(.+)$/m);
          if (!dataLine) continue;
          const evtName = eventLine ? eventLine[1] : "message";
          let payload;
          try { payload = JSON.parse(dataLine[1]); }
          catch { continue; }
          if (evtName === "init") {
            setProgress((p) => ({
              ...p,
              order: payload.drawings,
              drawings: payload.drawings.reduce((acc, d) => (
                { ...acc, [d]: { status: "pending" } }), {}),
              total: payload.total,
            }));
          } else if (evtName === "progress") {
            setProgress((p) => ({
              ...p,
              drawings: {
                ...p.drawings,
                [payload.drawing]: {
                  status: payload.status,
                  substitutions: payload.substitutions,
                  error: payload.error,
                },
              },
            }));
          } else if (evtName === "complete") {
            downloadUrl = payload.download_url;
            setProgress((p) => ({
              ...p, complete: true,
              downloadUrl: payload.download_url,
              stats: payload.stats,
            }));
          } else if (evtName === "error") {
            toast.error(payload.error || "Generation failed");
            setProgress((p) => ({ ...p, error: payload.error }));
          }
        }
      }
      if (downloadUrl) {
        const blob = await getBlob(
          downloadUrl.replace(/^\/api/, ""));
        downloadBlob(blob, `schematic_gen_${sessionId}.zip`);
        toast.success("Batch complete — ZIP downloaded.");
        await refreshSession();
      }
    } catch (e) {
      toast.error(`Generation failed: ${e?.message || e}`);
    } finally {
      setGenerating(false);
    }
  }, [sessionId, refreshSession]);

  const onResetSession = useCallback(async () => {
    if (!sessionId) return;
    try {
      await api.delete(`/schematic-gen/sessions/${sessionId}`);
    } catch { /* non-blocking */ }
    setSessionId(null);
    setSession(null);
    await bootstrap();
  }, [sessionId, bootstrap]);

  // Library actions
  const onSaveTemplateToLibrary = useCallback(async (files) => {
    if (!files?.length) return;
    const isAllowed = (f) => /\.(dwg|dxf|csv)$/i.test(f.name);
    const bad = Array.from(files).filter((f) => !isAllowed(f));
    if (bad.length) {
      toast.error("Library only accepts .dwg, .dxf, and .csv files.");
    }
    const ok = Array.from(files).filter(isAllowed);
    if (!ok.length) return;
    try {
      const form = new FormData();
      ok.forEach((f) => form.append("files", f));
      const { data } = await api.post(
        "/schematic-gen/library/templates", form,
        { headers: { "Content-Type": "multipart/form-data" } });
      const n = (data.accepted || []).length;
      if (n) toast.success(`Saved ${n} file${n === 1 ? "" : "s"} to library.`);
      await refreshLibrary();
    } catch (e) {
      toast.error(`Library save failed: ${e?.response?.data?.detail || e?.message}`);
    } finally {
      if (libInputRef.current) libInputRef.current.value = "";
    }
  }, [refreshLibrary]);

  const onImportLibraryToSession = useCallback(async (tid, kind) => {
    if (!sessionId) return;
    try {
      await api.post(
        `/schematic-gen/sessions/${sessionId}/upload-library/${tid}`);
      toast.success(kind === "csv"
        ? "CSV loaded into session."
        : "Template added to session.");
      await refreshSession();
    } catch (e) {
      toast.error(`Import failed: ${e?.response?.data?.detail || e?.message}`);
    }
  }, [sessionId, refreshSession]);

  const onDownloadLibraryTemplate = useCallback(async (tid, name) => {
    try {
      const blob = await getBlob(
        `/schematic-gen/library/templates/${tid}/download`);
      downloadBlob(blob, name);
    } catch (e) {
      toast.error(`Download failed: ${e?.response?.data?.detail || e?.message}`);
    }
  }, []);

  const onDeleteLibraryTemplate = useCallback(async (tid) => {
    if (!window.confirm("Delete this item from your library?")) return;
    try {
      await api.delete(`/schematic-gen/library/templates/${tid}`);
      await refreshLibrary();
    } catch (e) {
      toast.error(`Delete failed: ${e?.response?.data?.detail || e?.message}`);
    }
  }, [refreshLibrary]);

  const onDownloadSampleCsv = useCallback(async () => {
    const blob = await getBlob("/schematic-gen/sample-csv");
    downloadBlob(blob, "schematic_gen_sample.csv");
  }, []);

  const onDownloadSampleTemplate = useCallback(async () => {
    const blob = await getBlob("/schematic-gen/sample-template");
    downloadBlob(blob, "E-101_sample_template.dxf");
  }, []);

  const onPreviewFirst = useCallback(async () => {
    if (!sessionId) return;
    setPreviewing(true);
    // v2.50 papercut fix — don't null out previewData during re-fetch;
    // keep the modal mounted so the DiffTable's showDiff toggle stays
    // in its current state across inline edits.
    try {
      const { data } = await api.post(
        `/schematic-gen/sessions/${sessionId}/preview`);
      setPreviewData(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.message) toast.error(detail.message);
      else toast.error(`Preview failed: ${detail || e?.message}`);
    } finally {
      setPreviewing(false);
    }
  }, [sessionId]);

  const validation = session?.validation;
  const templates = session?.templates || [];
  const canGenerate = validation?.ok && templates.length > 0 && session?.csv_grid_id;
  const canSaveAsPack = templates.length > 0 && session?.csv_grid_id;

  const matchedCount = validation?.matched_pairs?.length || 0;

  const dragOverStyle = { borderColor: "#00E5FF" };

  return (
    <div className="space-y-6" data-testid="schematic-gen-panel">
      {/* Header + sample downloads */}
      <div className="border border-white/10 bg-[#12151C] p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="label-tag mb-1">// v2.44 · schematic-gen</div>
            <h2 className="font-display text-2xl font-black tracking-tight">Schematic Generator</h2>
            <p className="text-sm text-gray-400 mt-2 max-w-3xl">
              Generate a batch of drawings from a single template family and a CSV of per-drawing values. Every column in row&nbsp;0 of the CSV must match a text string on the templates — the generator swaps each match with the row&nbsp;value and outputs one drawing per matching drawing-number.
            </p>
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            <button onClick={onDownloadSampleCsv}
                    className="btn-secondary inline-flex items-center gap-2 text-xs"
                    data-testid="schgen-sample-csv-btn">
              <FileCsv size={14} /> Sample CSV
            </button>
            <button onClick={onDownloadSampleTemplate}
                    className="btn-secondary inline-flex items-center gap-2 text-xs"
                    data-testid="schgen-sample-template-btn">
              <DownloadSimple size={14} /> Sample DXF template
            </button>
            <button onClick={onResetSession}
                    className="btn-ghost inline-flex items-center gap-2 text-xs"
                    data-testid="schgen-reset-btn">
              <ArrowClockwise size={14} /> Reset session
            </button>
          </div>
        </div>
      </div>

      {/* Uploader */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        style={dragOver ? dragOverStyle : {}}
        className="border-2 border-dashed border-white/15 bg-[#0F1218] p-8 text-center hover:border-[#00E5FF]/60 transition-colors"
        data-testid="schgen-dropzone">
        <UploadSimple size={32} className="text-[#00E5FF] mx-auto" />
        <div className="mt-3 text-sm text-gray-300 font-mono">
          Drop <b>.csv</b>, <b>.dwg</b>, or <b>.dxf</b> files — or an entire <b>folder</b> — here
        </div>
        <div className="mt-1 text-xs text-gray-500 font-mono">
          One CSV. As many templates as you need — filename must match a drawing-number in column&nbsp;0 of the CSV. Folders are walked recursively.
        </div>
        <div className="mt-4 flex items-center justify-center gap-2 flex-wrap">
          <input type="file" ref={fileInputRef} multiple hidden
                 accept=".csv,.dwg,.dxf"
                 onChange={(e) => handleFiles(e.target.files)}
                 data-testid="schgen-file-input" />
          {/* v2.46 — Folder picker (webkitdirectory) — Chromium + Firefox.
              These non-standard attributes need lowercase in React,
              and both must be present to activate folder-picker mode. */}
          <input type="file" ref={folderInputRef} hidden
                 multiple
                 {...{ webkitdirectory: "", directory: "" }}
                 onChange={(e) => handleFiles(e.target.files)}
                 data-testid="schgen-folder-input" />
          <button className="btn-primary inline-flex items-center gap-2"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading || !sessionId}
                  data-testid="schgen-browse-btn">
            {uploading ? <Spinner className="animate-spin" size={14} />
                       : <UploadSimple size={14} />}
            {uploading ? "Uploading…" : "Choose files"}
          </button>
          <button className="btn-secondary inline-flex items-center gap-2"
                  onClick={() => folderInputRef.current?.click()}
                  disabled={uploading || !sessionId}
                  data-testid="schgen-browse-folder-btn"
                  title="Pick a whole folder — the app will find the CSV + templates">
            <FolderOpen size={14} /> Choose folder
          </button>
        </div>
      </div>

      {/* v2.55 — Fork history ribbon: audit trail for sessions
          bootstrapped from an org CSV starter (± template pack). */}
      {session?.forked_from_name && (
        <div className="border-l-2 border-[#00E5FF] bg-[#00E5FF]/5 px-3 py-2 flex items-center justify-between gap-3 text-xs font-mono"
             data-testid="schgen-fork-ribbon">
          <div className="flex items-center gap-2 min-w-0 flex-wrap">
            <GitFork size={13} className="text-[#00E5FF] shrink-0" />
            <span className="text-gray-500 uppercase text-[9px] tracking-wider">Forked from</span>
            <span className="text-[#00E5FF] truncate"
                  data-testid="schgen-fork-ribbon-starter">
              {session.forked_from_name}
            </span>
            {session.forked_from_org_csv && (
              <button onClick={() => onDownloadOrgCsv(
                        session.forked_from_org_csv,
                        session.csv_filename || "starter.csv")}
                      className="text-gray-500 hover:text-[#00E5FF] shrink-0"
                      title="Download the source CSV starter"
                      data-testid="schgen-fork-ribbon-download-csv">
                <DownloadSimple size={12} />
              </button>
            )}
            {session.forked_from_pack_name && (
              <>
                <span className="text-gray-600">+</span>
                <Package size={12} className="text-[#FFCC00] shrink-0" />
                <span className="text-[#FFCC00] truncate"
                      data-testid="schgen-fork-ribbon-pack">
                  {session.forked_from_pack_name}
                </span>
                {session.forked_from_pack && (
                  <button onClick={() => onDownloadPack(
                            session.forked_from_pack,
                            session.forked_from_pack_name)}
                          className="text-gray-500 hover:text-[#FFCC00] shrink-0"
                          title="Download the source template pack"
                          data-testid="schgen-fork-ribbon-download-pack">
                    <DownloadSimple size={12} />
                  </button>
                )}
              </>
            )}
          </div>
          <div className="text-[9px] font-mono text-gray-500 shrink-0">
            audit trail
          </div>
        </div>
      )}

      {/* Uploaded files */}
      {(session?.csv_filename || templates.length > 0) && (
        <div className="border border-white/10 bg-[#12151C] p-4"
             data-testid="schgen-files-list">
          <div className="font-mono text-xs uppercase tracking-wider text-gray-500 mb-3">Uploaded files</div>
          <ul className="space-y-1.5">
            {session?.csv_filename && (
              <li className="flex items-center justify-between gap-3 text-xs font-mono"
                  data-testid="schgen-file-csv">
                <div className="flex items-center gap-2 truncate min-w-0">
                  <FileCsv size={14} className="text-[#00E5FF] shrink-0" />
                  <span className="text-gray-200 truncate">{session.csv_filename}</span>
                  <span className="text-[#00E5FF]/70">[CSV]</span>
                </div>
                <button onClick={() => removeFile("csv")}
                        className="text-gray-500 hover:text-white shrink-0"
                        data-testid="schgen-remove-csv">
                  <X size={14} />
                </button>
              </li>
            )}
            {templates.map((t) => (
              <li key={t.name}
                  className="flex items-center justify-between gap-3 text-xs font-mono"
                  data-testid={`schgen-file-tpl-${t.name}`}>
                <div className="flex items-center gap-2 truncate min-w-0">
                  <DownloadSimple size={14} className="text-gray-500 shrink-0" />
                  <span className="text-gray-200 truncate">{t.name}</span>
                  <span className="text-gray-500">({(t.size / 1024).toFixed(1)} KB)</span>
                </div>
                <button onClick={() => removeFile(t.name)}
                        className="text-gray-500 hover:text-white shrink-0"
                        data-testid={`schgen-remove-${t.name}`}>
                  <X size={14} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Validation report */}
      {validation && (
        <div className="border border-white/10 bg-[#12151C] p-4"
             data-testid="schgen-validation">
          <div className="flex items-center justify-between mb-3">
            <div className="font-mono text-xs uppercase tracking-wider text-gray-500">
              Pre-flight check
            </div>
            <span className={`text-xs font-mono ${validation.ok ? "text-[#00E5FF]" : "text-[#FF6B6B]"}`}>
              {validation.ok
                ? <CheckCircle size={14} className="inline" weight="fill" />
                : <Warning size={14} className="inline" weight="fill" />
              }{" "}
              {validation.ok ? "READY" : "ISSUES FOUND"}
            </span>
          </div>

          {validation.errors?.length > 0 && (
            <div className="border-l-2 border-[#FF6B6B] pl-3 py-2 mb-3 bg-[#FF6B6B]/5"
                 data-testid="schgen-errors">
              <div className="font-mono text-[10px] uppercase tracking-wider text-[#FF6B6B] mb-1">
                Errors — fix these before generating
              </div>
              <ul className="text-xs text-gray-200 space-y-1">
                {validation.errors.map((e, i) => (
                  <li key={i} className="font-mono">• {e}</li>
                ))}
              </ul>
            </div>
          )}

          {validation.warnings?.length > 0 && (
            <div className="border-l-2 border-[#FFCC00] pl-3 py-2 mb-3 bg-[#FFCC00]/5"
                 data-testid="schgen-warnings">
              <div className="font-mono text-[10px] uppercase tracking-wider text-[#FFCC00] mb-1">Warnings</div>
              <ul className="text-xs text-gray-200 space-y-1">
                {validation.warnings.map((w, i) => (
                  <li key={i} className="font-mono">• {w}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <StatCard label="Matched" value={matchedCount} data-testid="schgen-stat-matched" />
            <StatCard label="Templates" value={validation.template_count || 0} />
            <StatCard label="CSV rows" value={validation.row_count || 0} />
            <StatCard label="Columns" value={validation.csv_columns?.length || 0} />
          </div>

          {validation.csv_columns?.length > 0 && (
            <div className="mt-3 border border-white/10 p-2 bg-[#0F1218]">
              <div className="font-mono text-[10px] uppercase tracking-wider text-gray-500 mb-1">
                CSV columns (row 0)
              </div>
              <div className="flex flex-wrap gap-1.5">
                {validation.csv_columns.map((c, i) => (
                  <span key={c} className={`text-[10px] font-mono px-1.5 py-0.5 border ${i === 0 ? "border-[#00E5FF]/50 text-[#00E5FF]" : "border-white/10 text-gray-300"}`}>
                    {i === 0 ? `📎 ${c}` : c}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Generate + Preview + Save-as-pack */}
      <div className="flex justify-end gap-2 flex-wrap">
        <button onClick={onPreviewFirst}
                disabled={!canGenerate || previewing || generating}
                className="btn-ghost inline-flex items-center gap-2 disabled:opacity-40"
                title="Render a PNG thumbnail of the first drawing before running the full batch"
                data-testid="schgen-preview-btn">
          {previewing ? <Spinner className="animate-spin" size={16} />
                      : <Eye size={16} />}
          {previewing ? "Rendering…" : "Preview first"}
        </button>
        <button onClick={() => setShowSavePackModal(true)}
                disabled={!canSaveAsPack}
                className="btn-secondary inline-flex items-center gap-2 disabled:opacity-40"
                title="Bundle the CSV + all templates into a reusable org pack"
                data-testid="schgen-save-pack-btn">
          <Package size={16} />
          Save as pack
        </button>
        <button onClick={onGenerate}
                disabled={!canGenerate || generating}
                className="btn-primary inline-flex items-center gap-2 disabled:opacity-40"
                data-testid="schgen-generate-btn">
          {generating ? <Spinner className="animate-spin" size={16} />
                      : <DownloadSimple size={16} />}
          {generating ? "Generating…"
            : `Generate ${matchedCount} drawing${matchedCount === 1 ? "" : "s"} → ZIP`}
        </button>
      </div>

      {/* v2.46 — Live batch-progress card (only during / after streaming) */}
      {progress && (
        <div className="border border-white/10 bg-[#12151C] p-4"
             data-testid="schgen-progress">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="font-mono text-xs uppercase tracking-wider text-gray-400">
                Batch progress
              </div>
              <div className="text-[10px] font-mono text-gray-500">
                Live stream from the server — one row per drawing
              </div>
            </div>
            {progress.complete && progress.stats && (
              <div className="text-xs font-mono text-[#00E5FF] inline-flex items-center gap-2"
                   data-testid="schgen-progress-complete">
                <CheckCircle size={14} weight="fill" />
                {progress.stats.generated} generated · {progress.stats.substitutions} substitutions
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
            {(progress.order || []).map((dwg) => {
              const s = progress.drawings?.[dwg] || { status: "pending" };
              const tone = s.status === "done"        ? "text-[#00E5FF] border-[#00E5FF]/40"
                          : s.status === "error"       ? "text-[#FF6B6B] border-[#FF6B6B]/40"
                          : s.status === "pending"     ? "text-gray-500 border-white/10"
                          : "text-[#FFCC00] border-[#FFCC00]/40";
              const label = s.status === "converting"   ? "Converting…"
                            : s.status === "substituting" ? "Substituting…"
                            : s.status === "done"         ? "Done"
                            : s.status === "error"        ? "Error"
                            : "Pending";
              return (
                <div key={dwg}
                     className={`border px-2 py-1.5 bg-[#0F1218] flex items-center justify-between gap-2 ${tone}`}
                     data-testid={`schgen-progress-row-${dwg}`}
                     data-status={s.status}>
                  <div className="flex items-center gap-1.5 min-w-0">
                    {s.status === "converting" || s.status === "substituting"
                      ? <Spinner size={11} className="animate-spin shrink-0" />
                      : s.status === "done"
                        ? <CheckCircle size={11} weight="fill" className="shrink-0" />
                        : s.status === "error"
                          ? <Warning size={11} weight="fill" className="shrink-0" />
                          : <span className="w-[11px] h-[11px] rounded-full border border-current shrink-0" />}
                    <span className="text-xs font-mono truncate">{dwg}</span>
                  </div>
                  <span className="text-[10px] font-mono shrink-0">
                    {label}
                    {s.status === "done" && s.substitutions != null
                      && ` · ${s.substitutions}×`}
                  </span>
                </div>
              );
            })}
          </div>
          {progress.error && (
            <div className="mt-3 border-l-2 border-[#FF6B6B] pl-3 py-2 bg-[#FF6B6B]/5 text-xs font-mono text-[#FF6B6B]"
                 data-testid="schgen-progress-error">
              {progress.error}
            </div>
          )}
        </div>
      )}


      {/* Library */}
      <div className="border border-white/10 bg-[#12151C] p-4"
           data-testid="schgen-library">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <BookOpen size={16} className="text-[#00E5FF]" />
            <div>
              <div className="font-mono text-xs uppercase tracking-wider text-gray-400">
                My template library
              </div>
              <div className="text-[10px] font-mono text-gray-500">
                Persistent — templates and CSVs saved here stay across sessions
              </div>
            </div>
          </div>
          <div>
            <input type="file" ref={libInputRef} multiple hidden
                   accept=".dwg,.dxf,.csv"
                   onChange={(e) => onSaveTemplateToLibrary(e.target.files)}
                   data-testid="schgen-lib-file-input" />
            <button className="btn-secondary inline-flex items-center gap-2 text-xs"
                    onClick={() => libInputRef.current?.click()}
                    data-testid="schgen-lib-save-btn">
              <FloppyDisk size={14} /> Save file
            </button>
          </div>
        </div>
        {libraryLoading ? (
          <div className="text-xs font-mono text-gray-500">Loading…</div>
        ) : library.length === 0 ? (
          <div className="text-xs font-mono text-gray-500 border-l-2 border-gray-700 pl-3 py-1">
            Nothing saved yet — click <b>Save file</b> above to add a DWG/DXF template or a reusable CSV.
          </div>
        ) : (
          <ul className="space-y-1.5">
            {library.map((t) => {
              const isCsv = t.kind === "csv";
              return (
              <li key={t.id}
                  className="flex items-center justify-between gap-2 text-xs font-mono border border-white/5 bg-[#0F1218] px-2 py-1.5"
                  data-testid={`schgen-lib-item-${t.id}`}>
                <div className="flex items-center gap-2 truncate min-w-0">
                  {isCsv
                    ? <FileCsv size={13} className="text-[#FFCC00] shrink-0" />
                    : <DownloadSimple size={13} className="text-gray-500 shrink-0" />}
                  <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 border shrink-0 ${
                    isCsv
                      ? "border-[#FFCC00]/40 text-[#FFCC00]"
                      : "border-[#00E5FF]/40 text-[#00E5FF]"}`}
                        data-testid={`schgen-lib-kind-${t.id}`}>
                    {isCsv ? "CSV" : "TPL"}
                  </span>
                  <span className="text-gray-200 truncate">{t.name}</span>
                  <span className="text-gray-500 shrink-0">({(t.size / 1024).toFixed(1)} KB)</span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => onImportLibraryToSession(t.id, t.kind)}
                          className="text-[10px] font-mono px-2 py-1 border border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/10"
                          data-testid={`schgen-lib-import-${t.id}`}>
                    Use in session
                  </button>
                  <button onClick={() => onDownloadLibraryTemplate(t.id, t.name)}
                          className="text-gray-400 hover:text-white p-1"
                          title="Download">
                    <DownloadSimple size={13} />
                  </button>
                  <button onClick={() => onDeleteLibraryTemplate(t.id)}
                          className="text-gray-400 hover:text-[#FF6B6B] p-1"
                          title="Delete"
                          data-testid={`schgen-lib-delete-${t.id}`}>
                    <Trash size={13} />
                  </button>
                </div>
              </li>
            );})}
          </ul>
        )}
      </div>

      {/* Org CSV starters — pinned per-org project master CSVs ──────── */}
      <div className="border border-white/10 bg-[#12151C] p-4"
           data-testid="schgen-orgcsvs">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <FileCsv size={16} className="text-[#FFCC00]" />
            <div>
              <div className="font-mono text-xs uppercase tracking-wider text-gray-400">
                Org CSV starters
              </div>
              <div className="text-[10px] font-mono text-gray-500">
                Admins pin canonical project CSVs — every teammate starts one click away
              </div>
            </div>
          </div>
          <button className="btn-secondary inline-flex items-center gap-2 text-xs"
                  onClick={() => setShowPinCsvModal(true)}
                  data-testid="schgen-orgcsv-pin-btn">
            <FloppyDisk size={14} /> Pin CSV starter
          </button>
        </div>
        {orgCsvsLoading ? (
          <div className="text-xs font-mono text-gray-500">Loading…</div>
        ) : orgCsvs.length === 0 ? (
          <div className="text-xs font-mono text-gray-500 border-l-2 border-gray-700 pl-3 py-1">
            No CSV starters pinned yet. Admins can click <b>Pin CSV starter</b> above to make a project-master CSV available to every teammate.
          </div>
        ) : (
          <ul className="space-y-1.5">
            {orgCsvs.map((c) => (
              <li key={c.id}
                  className="flex items-center justify-between gap-2 text-xs font-mono border border-white/5 bg-[#0F1218] px-2 py-1.5"
                  data-testid={`schgen-orgcsv-item-${c.id}`}>
                <div className="flex items-center gap-2 truncate min-w-0">
                  <FileCsv size={13} className="text-[#FFCC00] shrink-0" />
                  <span className="text-gray-200 truncate">{c.name}</span>
                  <span className="text-gray-500 truncate">
                    · {c.filename} ({(c.size / 1024).toFixed(1)} KB)
                  </span>
                  {c.description ? (
                    <span className="text-gray-600 italic truncate hidden md:inline">
                      — {c.description}
                    </span>
                  ) : null}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <div className="relative inline-flex">
                    <button onClick={() => onForkFromOrgCsv(c.id, c.name)}
                            className="text-[10px] font-mono px-2 py-1 border border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/10 inline-flex items-center gap-1"
                            title="Create a fresh session pre-seeded with this CSV"
                            data-testid={`schgen-orgcsv-fork-${c.id}`}>
                      <GitFork size={11} /> Fork
                    </button>
                    {packs.length > 0 ? (
                      <button onClick={() => setForkPackMenuFor(
                                forkPackMenuFor === c.id ? null : c.id)}
                              className="text-[10px] font-mono px-1.5 py-1 border border-l-0 border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/10 inline-flex items-center"
                              title="Fork + attach an org template pack"
                              data-testid={`schgen-orgcsv-fork-menu-${c.id}`}>
                        <CaretDown size={11} />
                      </button>
                    ) : null}
                    {forkPackMenuFor === c.id && packs.length > 0 ? (
                      <div className="absolute right-0 top-full mt-1 z-20 min-w-[240px] max-w-[320px] bg-[#0F1218] border border-white/15 shadow-lg"
                           data-testid={`schgen-orgcsv-fork-menu-panel-${c.id}`}>
                        <div className="px-3 py-2 border-b border-white/10 text-[10px] font-mono uppercase tracking-wider text-gray-500">
                          Fork + attach pack
                        </div>
                        <ul className="max-h-64 overflow-y-auto">
                          {packs.map((p) => (
                            <li key={p.id}>
                              <button onClick={() => {
                                        setForkPackMenuFor(null);
                                        onForkFromOrgCsv(c.id, c.name, p.id);
                                      }}
                                      className="w-full text-left px-3 py-2 text-xs font-mono text-gray-200 hover:bg-white/5 flex items-center justify-between gap-2"
                                      data-testid={`schgen-orgcsv-fork-pack-${c.id}-${p.id}`}>
                                <span className="truncate">{p.name}</span>
                                <span className="text-[10px] text-gray-500 shrink-0">
                                  {p.template_count} tpl
                                </span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </div>
                  <button onClick={() => onAttachOrgCsv(c.id, c.name)}
                          className="text-[10px] font-mono px-2 py-1 border border-[#FFCC00]/40 text-[#FFCC00] hover:bg-[#FFCC00]/10"
                          data-testid={`schgen-orgcsv-attach-${c.id}`}>
                    Use in session
                  </button>
                  <button onClick={() => onDownloadOrgCsv(c.id, c.filename)}
                          className="text-gray-400 hover:text-white p-1"
                          title="Download">
                    <DownloadSimple size={13} />
                  </button>
                  {c.can_manage ? (
                    <button onClick={() => onDeleteOrgCsv(c.id, c.name)}
                            className="text-gray-400 hover:text-[#FF6B6B] p-1"
                            title="Delete"
                            data-testid={`schgen-orgcsv-delete-${c.id}`}>
                      <Trash size={13} />
                    </button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Org packs — bundle CSV + templates into a reusable pack ────── */}
      <div className="border border-white/10 bg-[#12151C] p-4"
           data-testid="schgen-packs">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Package size={16} className="text-[#FFCC00]" />
            <div>
              <div className="font-mono text-xs uppercase tracking-wider text-gray-400">
                Org template packs
              </div>
              <div className="text-[10px] font-mono text-gray-500">
                Full CSV + template bundles — one-click import for anyone in your org
              </div>
            </div>
          </div>
          <button onClick={() => setShowSavePackModal(true)}
                  disabled={!canSaveAsPack}
                  className="btn-secondary inline-flex items-center gap-2 text-xs disabled:opacity-40"
                  data-testid="schgen-packs-save-btn"
                  title={canSaveAsPack ? "Save current session as a shared pack" : "Add a CSV + template first"}>
            <ShareNetwork size={14} /> Save current session as pack
          </button>
        </div>
        {packsLoading ? (
          <div className="text-xs font-mono text-gray-500">Loading…</div>
        ) : packs.length === 0 ? (
          <div className="text-xs font-mono text-gray-500 border-l-2 border-gray-700 pl-3 py-1">
            No org packs yet. Upload a CSV + templates above, then click <b>Save current session as pack</b>.
          </div>
        ) : (
          <ul className="space-y-1.5">
            {packs.map((p) => (
              <li key={p.id}
                  className="flex items-center justify-between gap-2 text-xs font-mono border border-white/5 bg-[#0F1218] px-2 py-2"
                  data-testid={`schgen-pack-item-${p.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Package size={13} className="text-[#FFCC00] shrink-0" />
                    <span className="text-white font-bold truncate">{p.name}</span>
                    <span className="text-gray-500 shrink-0">
                      · {p.template_count} template{p.template_count === 1 ? "" : "s"}
                      · {(p.size / 1024).toFixed(1)} KB
                    </span>
                  </div>
                  {p.description && (
                    <div className="text-gray-400 pl-5 mt-0.5 truncate"
                          title={p.description}>
                      {p.description}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => onImportPack(p.id)}
                          className="text-[10px] font-mono px-2 py-1 border border-[#FFCC00]/40 text-[#FFCC00] hover:bg-[#FFCC00]/10"
                          data-testid={`schgen-pack-import-${p.id}`}
                          title="Load this pack into the current session">
                    Import
                  </button>
                  <button onClick={() => onDownloadPack(p.id, p.name)}
                          className="text-gray-400 hover:text-white p-1"
                          title="Download the pack ZIP">
                    <DownloadSimple size={13} />
                  </button>
                  <button onClick={() => onDeletePack(p.id, p.name)}
                          className="text-gray-400 hover:text-[#FF6B6B] p-1"
                          title="Delete this pack (creator + org admins only)"
                          data-testid={`schgen-pack-delete-${p.id}`}>
                    <Trash size={13} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Save-as-pack modal ───────────────────────────────────────── */}
      {showSavePackModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
             data-testid="schgen-save-pack-modal"
             onClick={(e) => {
               if (e.target === e.currentTarget && !savingPack) {
                 setShowSavePackModal(false);
               }
             }}>
          <div className="bg-[#12151C] border border-white/10 max-w-md w-full p-6">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <div className="label-tag mb-1">// pack</div>
                <h3 className="font-display text-xl font-black">Save as pack</h3>
                <p className="text-xs text-gray-400 mt-1">
                  Bundles the CSV + {templates.length} template{templates.length === 1 ? "" : "s"} into one reusable ZIP shared with your org.
                </p>
              </div>
              <button onClick={() => setShowSavePackModal(false)}
                      disabled={savingPack}
                      className="text-gray-500 hover:text-white">
                <X size={18} />
              </button>
            </div>
            <label className="block text-[10px] font-mono uppercase tracking-wider text-gray-400 mb-1">
              Pack name
            </label>
            <input value={packName}
                    onChange={(e) => setPackName(e.target.value)}
                    placeholder="Substation drawings — Feb 2026"
                    disabled={savingPack}
                    className="w-full bg-[#0F1218] border border-white/15 text-white font-mono text-sm px-3 py-2 focus:border-[#00E5FF] focus:outline-none disabled:opacity-50"
                    autoFocus
                    data-testid="schgen-pack-name-input" />
            <label className="block text-[10px] font-mono uppercase tracking-wider text-gray-400 mb-1 mt-3">
              Description (optional)
            </label>
            <textarea value={packDescription}
                        onChange={(e) => setPackDescription(e.target.value)}
                        placeholder="What this pack is for, who it's used by, etc."
                        disabled={savingPack}
                        rows={3}
                        className="w-full bg-[#0F1218] border border-white/15 text-white font-mono text-xs px-3 py-2 focus:border-[#00E5FF] focus:outline-none disabled:opacity-50 resize-none"
                        data-testid="schgen-pack-description-input" />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowSavePackModal(false)}
                      disabled={savingPack}
                      className="btn-ghost text-xs">
                Cancel
              </button>
              <button onClick={onSaveAsPack}
                      disabled={savingPack || !packName.trim()}
                      className="btn-primary inline-flex items-center gap-2 text-xs disabled:opacity-40"
                      data-testid="schgen-pack-save-confirm-btn">
                {savingPack ? <Spinner size={14} className="animate-spin" />
                            : <FloppyDisk size={14} />}
                {savingPack ? "Saving…" : "Save pack"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* v2.52 — Pin CSV starter modal (admin only) */}
      {showPinCsvModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
             data-testid="schgen-pin-csv-modal"
             onClick={(e) => {
               if (e.target === e.currentTarget && !pinningCsv) {
                 setShowPinCsvModal(false);
               }
             }}>
          <div className="bg-[#12151C] border border-white/10 max-w-md w-full p-6">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <div className="label-tag mb-1">// org csv starter</div>
                <h3 className="font-display text-xl font-black">Pin CSV starter</h3>
                <p className="text-xs text-gray-400 mt-1">
                  Only org admins and owners can pin. Anyone in the org can then attach it to a new session with one click.
                </p>
              </div>
              <button onClick={() => setShowPinCsvModal(false)}
                      disabled={pinningCsv}
                      className="text-gray-500 hover:text-white">
                <X size={18} />
              </button>
            </div>
            <label className="block text-[10px] font-mono uppercase tracking-wider text-gray-400 mb-1">
              Starter name
            </label>
            <input value={pinCsvName}
                    onChange={(e) => setPinCsvName(e.target.value)}
                    placeholder="Project master · Substation 2026"
                    disabled={pinningCsv}
                    className="w-full bg-[#0F1218] border border-white/15 text-white font-mono text-sm px-3 py-2 focus:border-[#FFCC00] focus:outline-none disabled:opacity-50"
                    autoFocus
                    data-testid="schgen-pin-csv-name-input" />
            <label className="block text-[10px] font-mono uppercase tracking-wider text-gray-400 mb-1 mt-3">
              Description (optional)
            </label>
            <textarea value={pinCsvDesc}
                        onChange={(e) => setPinCsvDesc(e.target.value)}
                        placeholder="What this starter is for, expected columns, etc."
                        disabled={pinningCsv}
                        rows={2}
                        className="w-full bg-[#0F1218] border border-white/15 text-white font-mono text-xs px-3 py-2 focus:border-[#FFCC00] focus:outline-none disabled:opacity-50 resize-none"
                        data-testid="schgen-pin-csv-desc-input" />
            <label className="block text-[10px] font-mono uppercase tracking-wider text-gray-400 mb-1 mt-3">
              CSV file
            </label>
            <input ref={pinCsvInputRef}
                    type="file"
                    accept=".csv"
                    disabled={pinningCsv}
                    onChange={(e) => setPinCsvFile(e.target.files?.[0] || null)}
                    className="w-full text-xs font-mono text-gray-300 file:mr-2 file:py-1 file:px-2 file:border file:border-white/15 file:bg-[#0F1218] file:text-gray-200 file:cursor-pointer hover:file:bg-white/5"
                    data-testid="schgen-pin-csv-file-input" />
            {pinCsvFile ? (
              <div className="text-[10px] font-mono text-gray-500 mt-1">
                Selected: {pinCsvFile.name} ({(pinCsvFile.size / 1024).toFixed(1)} KB)
              </div>
            ) : null}
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowPinCsvModal(false)}
                      disabled={pinningCsv}
                      className="btn-ghost text-xs">
                Cancel
              </button>
              <button onClick={onPinOrgCsv}
                      disabled={pinningCsv || !pinCsvName.trim() || !pinCsvFile}
                      className="btn-primary inline-flex items-center gap-2 text-xs disabled:opacity-40"
                      data-testid="schgen-pin-csv-confirm-btn">
                {pinningCsv ? <Spinner size={14} className="animate-spin" />
                            : <FloppyDisk size={14} />}
                {pinningCsv ? "Pinning…" : "Pin starter"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* v2.47 — Dry-run preview modal (renders first matched drawing) */}
      {previewData && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
             data-testid="schgen-preview-modal"
             onClick={(e) => {
               if (e.target === e.currentTarget) setPreviewData(null);
             }}>
          <div className="bg-[#12151C] border border-white/10 max-w-4xl w-full max-h-[90vh] overflow-auto p-6">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <div className="label-tag mb-1">// preview</div>
                <h3 className="font-display text-xl font-black">
                  Dry-run preview · <span className="text-[#00E5FF]">{previewData.drawing_no}</span>
                </h3>
                <p className="text-xs text-gray-400 mt-1">
                  First matched drawing with substitutions applied · <b className="text-white">{previewData.substitutions}</b> substitution{previewData.substitutions === 1 ? "" : "s"} · template <span className="font-mono text-gray-300">{previewData.template_name}</span>
                </p>
              </div>
              <button onClick={() => setPreviewData(null)}
                      className="text-gray-500 hover:text-white"
                      data-testid="schgen-preview-close-btn">
                <X size={18} />
              </button>
            </div>
            {previewData.png_base64 ? (
              <img src={`data:image/png;base64,${previewData.png_base64}`}
                   alt={`Preview of ${previewData.drawing_no}`}
                   className="w-full border border-white/10 bg-[#0F1218]"
                   data-testid="schgen-preview-image" />
            ) : (
              <div className="border-l-2 border-[#FFCC00] pl-3 py-3 bg-[#FFCC00]/5 text-xs font-mono text-[#FFCC00]"
                   data-testid="schgen-preview-error">
                {previewData.error || "Could not render this template as a PNG — the DXF may use features the preview renderer doesn't support. Generation will still work."}
              </div>
            )}

            {/* v2.48 — CSV-row diff panel (Compare rows side-by-side) */}
            {previewData.columns?.length > 0 && previewData.all_rows?.length > 0 && (
              <div className="mt-4 border border-white/10 bg-[#0F1218]"
                   data-testid="schgen-preview-diff">
                <button onClick={() => setShowDiff((v) => !v)}
                        className="w-full flex items-center justify-between gap-2 p-2 text-left hover:bg-white/[0.02]"
                        data-testid="schgen-preview-diff-toggle">
                  <div className="flex items-center gap-2">
                    <Table size={14} className="text-[#00E5FF]" />
                    <span className="text-xs font-mono uppercase tracking-wider text-gray-300">
                      Compare CSV rows ({previewData.all_rows.length} total)
                    </span>
                  </div>
                  <span className="text-[10px] font-mono text-gray-500">
                    {showDiff ? "− Hide" : "+ Show diff view"}
                  </span>
                </button>
                {showDiff && (
                  <DiffTable
                    columns={previewData.columns}
                    rows={previewData.all_rows}
                    currentDwg={previewData.drawing_no}
                    sessionId={sessionId}
                    onCellUpdated={onPreviewFirst}
                  />
                )}
              </div>
            )}
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setPreviewData(null)}
                      className="btn-ghost text-xs"
                      data-testid="schgen-preview-back-btn">
                Back to editor
              </button>
              <button onClick={() => { setPreviewData(null); onGenerate(); }}
                      disabled={!canGenerate || generating}
                      className="btn-primary inline-flex items-center gap-2 text-xs disabled:opacity-40"
                      data-testid="schgen-preview-looks-good-btn">
                <CheckCircle size={14} weight="fill" />
                Looks good — generate all {matchedCount}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

